import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetFunctionsByEventId, GetGuestsByEventId, GetCardTypes, GetExpenses,
  GetServicesByEventId, GetCostLines,
} from '../../api/api';
import {
  fmtDate, fmtTime, money, needsCard, isPastFunction, todayISO, EXPENSE_CATEGORIES, adderInitials, adderShortNames, adderLegend } from '../../utils/helpers';
import { makeCounter, linesOf, lineMath, isDueSoon, sourceOf, isLiveService } from '../../utils/costLines';
import {
  functionHeadcount, functionCards, rowFunctionIds, householdKey, extraPersonsFor,
} from '../../utils/headcount';

// BRIEF REPORT — the one sheet an organiser carries around:
//   1  how many people at each function      (functionHeadcount - the shared rule)
//   2  who invited how many                  (per member, and per function)
//   3  how many cards to order for distribution
//   4  what is paid in advance and what is still to be paid
//   5  bookings against the live count (a dinner booked for at least 100,
//      288 gifts ordered against the cards needed - utils/costLines.js)
// Every block, every function and every cost category has a CHECKBOX: unticking
// one drops it from the screen AND from the PDF / Excel, so the same page gives
// "all of it" and "only the catering" without a second report.
//
// Nothing here is new data: the numbers come from the calls the Headcount,
// Cards, Expenses and Services pages already make. Admin-only - the figures
// need the whole guest list, and a member only ever sees their own guests.

const num = (v) => Number(v || 0) || 0;
const round2 = (n) => Math.round(n * 100) / 100;
const typeFnIds = (t) => String(t.FunctionIds || '').split(',').map(v => v.trim()).filter(Boolean);

// Functions in the order they run: date, then start time, then id.
const byWhen = (a, b) => String(a.FunctionDate || '').localeCompare(String(b.FunctionDate || ''))
  || String(a.StartTime || '').localeCompare(String(b.StartTime || ''))
  || Number(a.FunctionId) - Number(b.FunctionId);

// What is handed over per household. The rows are the same people either way -
// a card, the gift that goes with it and a packet of sweets all go to the
// household, one each - but each walks round on its own sheet, with its own
// tick box, so two jobs are never ticked off on one piece of paper.
const HANDOUTS = {
  // A gift goes WITH a card, so it is counted like one - the extra copies a
  // head asks for carry a gift too. A packet of sweets is one per household.
  Cards: { title: 'Invitation cards', unit: 'cards', withExtras: true },
  Gifts: { title: 'Gifts (one with each card)', unit: 'gifts', withExtras: true },
  // A packet goes with every card handed out - the extra copies a head asked
  // for take one too (owner, 2026-10-20).
  Sweets: { title: 'Sweets (one half-kg packet each)', unit: 'packets', withExtras: true },
};

export const BLOCK_LABELS = {
  members: 'Members at each function',
  invitedBy: 'Who invited how many',
  cards: 'Cards required for distribution',
  money: 'Advance paid & balance to pay',
  bookings: 'Booked vs live count',
};

export function useBrief(event) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const canView = isAdmin;

  const [functions, setFunctions] = useState([]);
  const [guests, setGuests] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [services, setServices] = useState([]);
  const [costLines, setCostLines] = useState([]);
  const [loading, setLoading] = useState(true);

  // What the report shows. Everything is on until it is unticked.
  const [blocks, setBlocks] = useState({ members: true, invitedBy: true, cards: true, money: true, bookings: true });
  const [fnOff, setFnOff] = useState(() => new Set());     // functions left out
  const [catOff, setCatOff] = useState(() => new Set());   // cost categories left out
  const [withEstimates, setWithEstimates] = useState(true);

  const toggleBlock = (key) => setBlocks(b => ({ ...b, [key]: !b[key] }));
  const toggleOff = (setter) => (key) => setter(prev => {
    const next = new Set(prev);
    const k = String(key);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const toggleFunction = toggleOff(setFnOff);
  const toggleCategory = toggleOff(setCatOff);

  const load = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const [f, g, c, e, s] = await Promise.all([
        GetFunctionsByEventId({ EventId: event.EventId }),
        GetGuestsByEventId({ EventId: event.EventId }),
        GetCardTypes({ EventId: event.EventId }),
        GetExpenses({ EventId: event.EventId }),
        GetServicesByEventId({ EventId: event.EventId }),
      ]);
      setFunctions((Array.isArray(f) ? f : []).slice().sort(byWhen));
      setGuests(Array.isArray(g) ? g : []);
      setCardTypes(Array.isArray(c) ? c : []);
      setExpenses(Array.isArray(e) ? e : []);
      // A service still at Enquiry is a PRICE SOMEBODY SENT, not a booking:
      // the brief says what is owed, so quotations are left out of it.
      setServices((Array.isArray(s) ? s : []).filter(isLiveService));
      // Cost lines are an addition: an API without them must not break the brief.
      GetCostLines({ EventId: event.EventId }).then(d => setCostLines(Array.isArray(d) ? d : [])).catch(() => setCostLines([]));
    } catch {
      toast('Could not load the brief');
    } finally {
      setLoading(false);
    }
  }, [event, canView, toast]);
  useEffect(() => { load(); }, [load]);

  const fnOn = useCallback((id) => !fnOff.has(String(id)), [fnOff]);
  // A function whose date has passed is NOT in the brief: the sheet is carried
  // around to plan what is still to come. `functions` keeps them (a card type
  // may still name one); `liveFunctions` is what the report counts.
  const liveFunctions = useMemo(() => functions.filter(f => !isPastFunction(f)), [functions]);
  const pastFunctions = useMemo(() => functions.filter(f => isPastFunction(f)), [functions]);
  const shownFunctions = useMemo(() => liveFunctions.filter(f => fnOn(f.FunctionId)), [liveFunctions, fnOn]);
  // The sweets are handed out at the Thread Ceremony only (owner, 2026-10-20),
  // so the Sweets list of the cards block is that function's households.
  const sweetsFunction = useMemo(() => functions.find(f => /thread|janeu|upanayan/i.test(`${f.FunctionName || ''} ${f.FunctionTypeName || ''}`)) || null, [functions]);
  const twoSided = useMemo(() => guests.some(g => ['Groom', 'Bride', 'Male', 'Female'].includes(g.Side)), [guests]);

  // ── 1 · Members at each function ────────────────────────────────────────
  // Two numbers per function, because two different orders are placed on them:
  // PERSONS (plates, chairs) and CARDS (one per household - and with a card
  // goes whatever is handed over with it, a gift at the Thread Ceremony).
  const memberRows = useMemo(() => liveFunctions.map(f => {
    const h = functionHeadcount(guests, f.FunctionId, cardTypes);
    const c = functionCards(guests, f.FunctionId, cardTypes, needsCard);
    return {
      households: c.households,
      extraCards: c.extraCards,
      cards: c.cards,
      key: String(f.FunctionId),
      FunctionId: f.FunctionId,
      FunctionName: f.FunctionName,
      date: fmtDate(f.FunctionDate),
      time: [fmtTime(f.StartTime), fmtTime(f.EndTime)].filter(Boolean).join(' – '),
      venue: f.Venue || '',
      parties: h.parties,
      named: h.named,
      extra: h.extra,
      persons: h.persons,
      groom: h.bySide.Groom || 0,
      bride: h.bySide.Bride || 0,
      common: h.bySide.Common || 0,
      on: fnOn(f.FunctionId),
    };
  }), [liveFunctions, guests, cardTypes, fnOn]);

  const memberShown = useMemo(() => memberRows.filter(r => r.on), [memberRows]);
  const memberTotals = useMemo(() => memberShown.reduce((t, r) => ({
    parties: t.parties + r.parties, named: t.named + r.named, extra: t.extra + r.extra,
    persons: t.persons + r.persons, groom: t.groom + r.groom, bride: t.bride + r.bride,
    cards: t.cards + r.cards,
  }), { parties: 0, named: 0, extra: 0, persons: 0, groom: 0, bride: 0, cards: 0 }), [memberShown]);
  const biggest = useMemo(() => memberShown.reduce((best, r) => (r.persons > (best?.persons || 0) ? r : best), null), [memberShown]);

  // ONE name per person who invited guests. AddedByName is a snapshot taken
  // when the guest was saved, so the same user can sit in the data as both
  // "Ailani Sanjay" and "Sanjay Ailani" - sorting on the text alone splits one
  // person into two blocks of the list. Everything that groups by adder goes
  // through this instead: the id decides, the commonest spelling is shown.
  const adderName = useMemo(() => {
    const counts = new Map();
    guests.forEach(r => {
      const id = String(r.CreatedBy || '');
      const name = String(r.AddedByName || r.InvitedByName || '').trim();
      if (!id || !name) return;
      if (!counts.has(id)) counts.set(id, new Map());
      const m = counts.get(id);
      m.set(name, (m.get(name) || 0) + 1);
    });
    const out = new Map();
    counts.forEach((m, id) => {
      out.set(id, [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0]);
    });
    return (createdBy) => out.get(String(createdBy || '')) || '';
  }, [guests]);

  // ── 2 · Who invited how many ────────────────────────────────────────────
  // One line per person who invited guests. A guest row exists per function, so
  // everything is counted over DISTINCT guests; a count-only group counts the
  // persons it stands for (ExpectedCount).
  const invitedByRows = useMemo(() => {
    const people = new Map();
    const seenGuest = new Map();          // GuestId → the row kept for it
    guests.forEach(r => {
      const gid = String(r.GuestId);
      if (!seenGuest.has(gid)) seenGuest.set(gid, r);
    });
    const households = new Map();         // adder → Map(householdKey → extra cards)
    seenGuest.forEach(r => {
      const key = `${r.CreatedBy || 0}`;
      if (!people.has(key)) {
        people.set(key, {
          key,
          name: adderName(r.CreatedBy) || (num(r.CreatedBy) ? `User ${r.CreatedBy}` : '—'),
          mobile: r.AddedByMobile || '',
          isGuest: num(r.AddedByGuest) === 1,
          guests: 0, persons: 0, cards: 0, extraCards: 0, perFn: {},
        });
        households.set(key, new Map());
      }
      const p = people.get(key);
      const size = Math.max(1, num(r.ExpectedCount || 1));
      p.guests += 1;
      p.persons += size;
      if (needsCard(r)) {
        const hk = householdKey(r);
        const hm = households.get(key);
        const cards = num(r.ExtraCards);
        const prev = hm.get(hk);
        if (!prev || cards > prev.extraCards) hm.set(hk, { extraCards: cards, extraPersons: num(r.ExtraPersons) });
      }
      rowFunctionIds(r).forEach(id => { p.perFn[id] = (p.perFn[id] || 0) + size; });
    });
    people.forEach((p, key) => {
      const hm = households.get(key);
      p.cards = hm.size;
      hm.forEach(h => { p.extraCards += h.extraCards; p.persons += extraPersonsFor(h.extraCards, h.extraPersons); });
      p.totalCards = p.cards + p.extraCards;
    });
    return [...people.values()].sort((a, b) => b.persons - a.persons || a.name.localeCompare(b.name));
  }, [guests, adderName]);

  const invitedByTotals = useMemo(() => invitedByRows.reduce((t, r) => ({
    guests: t.guests + r.guests, persons: t.persons + r.persons, totalCards: t.totalCards + r.totalCards,
  }), { guests: 0, persons: 0, totalCards: 0 }), [invitedByRows]);

  // ── 3 · Cards required for distribution ─────────────────────────────────
  // One card per household (family, else mobile), the hosts' own family left
  // out - exactly the rule the Cards page prints by.
  const cardStats = useMemo(() => {
    const byKey = new Map();
    guests.filter(needsCard).forEach(r => {
      const k = householdKey(r);
      if (!byKey.has(k)) byKey.set(k, { fnIds: new Set(), extraCards: 0, extraPersons: 0, head: r });
      const c = byKey.get(k);
      if (num(r.FamilyId) && String(r.GuestId) === String(r.FamilyId)) c.head = r;
      rowFunctionIds(r).forEach(id => c.fnIds.add(String(id)));
      c.extraCards = Math.max(c.extraCards, num(r.ExtraCards));
      c.extraPersons = Math.max(c.extraPersons, num(r.ExtraPersons));
    });
    const list = [...byKey.values()];
    const extraCards = list.reduce((s, c) => s + c.extraCards, 0);
    const rows = cardTypes.map(t => {
      const ids = typeFnIds(t);
      const households = list.filter(c => ids.some(id => c.fnIds.has(id))).length;
      const extra = num(t.ForExtraCards) === 1 ? extraCards : 0;
      return {
        key: String(t.CardTypeId),
        name: t.CardTypeName,
        kind: t.Kind || 'Card',
        forFunctions: ids.map(id => functions.find(f => String(f.FunctionId) === id)?.FunctionName).filter(Boolean).join(', '),
        households, extra, total: households + extra,
      };
    });
    return {
      rows,
      households: list.length,
      extraCards,
      extraPersons: list.reduce((s, c) => s + extraPersonsFor(c.extraCards, c.extraPersons), 0),
      withoutType: cardTypes.length ? list.filter(c => !cardTypes.some(t => typeFnIds(t).some(id => c.fnIds.has(id)))).length : 0,
      ownFamily: new Set(guests.filter(r => !needsCard(r)).map(r => String(r.GuestId))).size,
      toOrder: rows.reduce((s, r) => s + r.total, 0),
    };
  }, [guests, cardTypes, functions]);

  // ── 4 · Advance paid & balance to pay ───────────────────────────────────
  // Expenses (Fixed, or rate × persons - live off the headcount above) PLUS the
  // engagements of the Services page, the same way the Expenses screen shows
  // them, grouped into one line per category so the sheet stays one page.
  const headcountOf = useCallback((fnId) => (fnId
    ? functionHeadcount(guests, fnId, cardTypes).persons : 0), [guests, cardTypes]);

  // An ITEMISED booking (cost lines) is split over its lines' categories - the
  // hotel's rooms under Travel & Stay, its hall under Hall / Venue, its dinner
  // under Food - and what was paid against it is shared out over those lines
  // in proportion to their amounts (an advance is paid on the booking, not on
  // a line). Each piece carries the booking's due date.
  const count = useMemo(() => makeCounter({ guests, cardTypes, functions }), [guests, cardTypes, functions]);
  const today = todayISO();
  const moneyLines = useMemo(() => {
    const out = [];
    const push = ({ category, title, estimate, total, paid, source, dueDate, lines }) => {
      if (!lines.length) {
        out.push({ category, title, estimate, total, paid, balance: round2(total - paid), source, dueDate });
        return;
      }
      const amounts = lines.map(l => lineMath(l, count).amount);
      const sum = amounts.reduce((s, a) => s + a, 0);
      lines.forEach((l, i) => {
        const share = sum > 0 ? round2(paid * (amounts[i] / sum)) : 0;
        out.push({
          category: String(l.Category || '').trim() || category, title: `${title} · ${l.Title}`,
          estimate, total: amounts[i], paid: share,
          balance: round2(amounts[i] - share), source, dueDate,
        });
      });
    };
    expenses.forEach(e => {
      const lines = linesOf(costLines, 'Expense', e.ExpenseId);
      const persons = e.PersonsSource === 'Headcount' && e.PersonsFunctionId
        ? headcountOf(e.PersonsFunctionId) : num(e.Persons);
      const total = e.AmountType === 'PerPerson' ? round2(num(e.Rate) * persons) : num(e.TotalAmount);
      push({
        category: e.Category || 'Other', title: e.Title, estimate: num(e.IsEstimate) === 1,
        total, paid: num(e.PaidAmount), source: 'expense', dueDate: e.DueDate || null, lines,
      });
    });
    services.forEach(sv => {
      push({
        category: sv.ServiceType || 'Services',
        title: sv.CompanyName || sv.ContactName || sv.ServiceType || 'Service',
        estimate: sv.Status === 'Enquiry',
        total: num(sv.TotalAmount), paid: num(sv.AdvancePaid),      // the API keeps it equal to the payments
        source: 'service', dueDate: sv.DueDate || null, lines: linesOf(costLines, 'Service', sv.ServiceId),
      });
    });
    return out;
  }, [expenses, services, headcountOf, costLines, count]);

  const moneyRows = useMemo(() => {
    const m = new Map();
    moneyLines.forEach(l => {
      if (!withEstimates && l.estimate) return;
      const k = l.category;
      if (!m.has(k)) m.set(k, { key: k, category: k, items: 0, estimates: 0, total: 0, paid: 0, balance: 0, fromServices: 0, nextDue: '', dueSoon: 0 });
      const g = m.get(k);
      g.items += 1; g.total += l.total; g.paid += l.paid; g.balance += l.balance;
      // The earliest date money in this category falls due.
      if (l.dueDate && l.balance > 0 && (!g.nextDue || String(l.dueDate) < g.nextDue)) g.nextDue = String(l.dueDate).slice(0, 10);
      if (isDueSoon(l.dueDate, l.balance, today)) g.dueSoon += l.balance;
      if (l.estimate) g.estimates += 1;
      if (l.source === 'service') g.fromServices += 1;
    });
    const order = (c) => { const i = EXPENSE_CATEGORIES.indexOf(c); return i < 0 ? 999 : i; };
    return [...m.values()]
      .map(g => ({ ...g, total: round2(g.total), paid: round2(g.paid), balance: round2(g.balance), dueSoon: round2(g.dueSoon), on: !catOff.has(g.key) }))
      .sort((a, b) => order(a.category) - order(b.category) || a.category.localeCompare(b.category));
  }, [moneyLines, withEstimates, catOff, today]);

  const moneyShown = useMemo(() => moneyRows.filter(r => r.on), [moneyRows]);
  const moneyTotals = useMemo(() => moneyShown.reduce((t, r) => ({
    total: round2(t.total + r.total), paid: round2(t.paid + r.paid), balance: round2(t.balance + r.balance),
    estimates: t.estimates + r.estimates, dueSoon: round2(t.dueSoon + r.dueSoon),
  }), { total: 0, paid: 0, balance: 0, estimates: 0, dueSoon: 0 }), [moneyShown]);

  // ── 5 · Booked vs live count ────────────────────────────────────────────
  // Every line whose quantity follows a live count: what was booked / ordered,
  // what the guest list says now, and what will be charged.
  const bookingRows = useMemo(() => {
    const parentName = (l) => (l.ParentType === 'Expense'
      ? expenses.find(e => String(e.ExpenseId) === String(l.ParentId))?.Title
      : (() => { const sv = services.find(x => String(x.ServiceId) === String(l.ParentId)); return sv && (sv.CompanyName || sv.ContactName || sv.ServiceType); })());
    return costLines
      .filter(l => l.LiveSource && String(l.Basis) !== 'Fixed')
      .filter(l => !l.FunctionId || fnOn(l.FunctionId))
      .map(l => ({ l, m: lineMath(l, count), parent: parentName(l) }))
      .filter(x => x.m.linked && x.parent)
      .map(({ l, m, parent }) => ({
        key: String(l.CostLineId),
        item: l.Title, booking: parent,
        forFn: l.FunctionId ? (functions.find(f => String(f.FunctionId) === String(l.FunctionId))?.FunctionName || '') : 'Whole event',
        follows: sourceOf(l.LiveSource).unit,
        rule: l.ChargeRule === 'Booked' ? 'ordered' : 'minimum',
        booked: m.booked, live: m.live, charged: m.qty, amount: m.amount,
        note: l.ChargeRule === 'Booked'
          ? (m.gap > 0 && m.booked > 0 ? `${m.gap} short` : m.gap < 0 ? `${-m.gap} spare` : (m.booked > 0 ? 'exact' : 'not ordered - follows the count'))
          : (m.live > m.booked ? `${m.live - m.booked} over the minimum` : `${m.booked - m.live} below the minimum`),
      }));
  }, [costLines, count, expenses, services, functions, fnOn]);
  const bookingColumns = useMemo(() => [
    { key: 'item', label: 'Item', pdfWidth: 34 },
    { key: 'booking', label: 'Booking', pdfWidth: 34 },
    { key: 'forFn', label: 'For', pdfWidth: 30 },
    { key: 'booked', label: 'Booked', align: 'num', pdfWidth: 16, render: (r) => `${r.booked} ${r.rule === 'ordered' ? '' : 'min'}`.trim() },
    { key: 'live', label: 'Live now', align: 'num', pdfWidth: 16, render: (r) => `${r.live} ${r.follows}` },
    { key: 'charged', label: 'Charged', align: 'num', pdfWidth: 16 },
    { key: 'amount', label: 'Amount', align: 'num', pdfWidth: 24, exportValue: (r) => money(r.amount), render: (r) => money(r.amount) },
    { key: 'note', label: 'Note', pdfWidth: 34 },
  ], []);

  // ── The report (screen = file) ──────────────────────────────────────────
  const fnNote = [
    fnOff.size ? `Functions: ${shownFunctions.map(f => f.FunctionName).join(', ') || 'none'}` : '',
    pastFunctions.length ? `${pastFunctions.map(f => `${f.FunctionName} (${fmtDate(f.FunctionDate)})`).join(', ')} ${pastFunctions.length === 1 ? 'is' : 'are'} over - not counted` : '',
  ].filter(Boolean).join(' · ');
  const catNote = catOff.size ? `Costs: ${moneyShown.map(r => r.category).join(', ') || 'none'}` : '';

  const memberColumns = useMemo(() => [
    { key: 'FunctionName', label: 'Function', pdfWidth: 46 },
    { key: 'date', label: 'Date', pdfWidth: 24 },
    { key: 'time', label: 'Time', pdfWidth: 26 },
    { key: 'cards', label: 'Cards / gifts', align: 'num', pdfWidth: 22 },
    { key: 'parties', label: 'Parties', align: 'num', pdfWidth: 18 },
    { key: 'named', label: 'Named persons', align: 'num', pdfWidth: 22 },
    { key: 'extra', label: 'Extra-card persons', align: 'num', pdfWidth: 22 },
    { key: 'persons', label: 'Persons (plates)', align: 'num', pdfWidth: 22 },
    ...(twoSided ? [
      { key: 'groom', label: 'Groom side', align: 'num', pdfWidth: 20 },
      { key: 'bride', label: 'Bride side', align: 'num', pdfWidth: 20 },
    ] : []),
  ], [twoSided]);

  const invitedByColumns = useMemo(() => [
    { key: 'name', label: 'Invited by', pdfWidth: 44, render: (r) => `${r.name}${r.isGuest ? ' (guest)' : ''}` },
    { key: 'mobile', label: 'Mobile', pdfWidth: 24 },
    { key: 'guests', label: 'Guests', align: 'num', pdfWidth: 16 },
    { key: 'persons', label: 'Persons', align: 'num', pdfWidth: 18 },
    { key: 'totalCards', label: 'Cards', align: 'num', pdfWidth: 16 },
    ...shownFunctions.map(f => ({
      key: `fn${f.FunctionId}`, label: f.FunctionName, align: 'num', pdfWidth: 18, keepEmpty: true,
      exportValue: (r) => String(r.perFn[String(f.FunctionId)] || 0),
      render: (r) => r.perFn[String(f.FunctionId)] || 0,
    })),
  ], [shownFunctions]);

  const cardColumns = useMemo(() => [
    { key: 'name', label: 'Card / insert', pdfWidth: 46 },
    { key: 'kind', label: 'Kind', pdfWidth: 18 },
    { key: 'forFunctions', label: 'Given for', pdfWidth: 60 },
    { key: 'households', label: 'Households', align: 'num', pdfWidth: 22 },
    { key: 'extra', label: 'Extra cards', align: 'num', pdfWidth: 20 },
    { key: 'total', label: 'To order', align: 'num', pdfWidth: 20 },
  ], []);

  const moneyColumns = useMemo(() => [
    { key: 'category', label: 'Category', pdfWidth: 46 },
    { key: 'items', label: 'Items', align: 'num', pdfWidth: 16 },
    { key: 'total', label: 'Amount', align: 'num', pdfWidth: 26, exportValue: (r) => money(r.total) },
    { key: 'paid', label: 'Paid in advance', align: 'num', pdfWidth: 26, exportValue: (r) => money(r.paid) },
    { key: 'balance', label: 'Balance to pay', align: 'num', pdfWidth: 26, exportValue: (r) => money(r.balance) },
    { key: 'estimates', label: 'Still estimates', align: 'num', pdfWidth: 20, exportValue: (r) => (r.estimates ? String(r.estimates) : '') },
    { key: 'nextDue', label: 'Next due', pdfWidth: 22, exportValue: (r) => (r.nextDue ? fmtDate(r.nextDue) : ''), render: (r) => (r.nextDue ? fmtDate(r.nextDue) : '') },
  ], []);

  // ── The names behind a number ───────────────────────────────────────────
  // Every figure in the brief is a count of people, and the question that
  // follows one is always "which people?". Asking a row for its list (right
  // click on the desktop, the ≡ button on a phone) builds that list here -
  // the guests of one function, or everyone one member invited.
  const [listFor, setListFor] = useState(null);     // { kind, id, label } | null
  const openList = (kind, id, label, what) => setListFor({ kind, id, label, what });
  const closeList = () => setListFor(null);

  // One line per HOUSEHOLD invited to a function - the list the cards, the
  // sweets and the gifts are handed out from. A family of three takes one
  // packet, exactly like a guest on their own: what is counted is the card,
  // not the people on it (the hosts' own family and count-only groups take
  // none at all - needsCard).
  // functionId null = the WHOLE event: every household that gets a card at all,
  // which is the number the cards are printed on.
  const householdList = useCallback((functionId) => {
    const open = new Set(functions.filter(f => !isPastFunction(f)).map(f => String(f.FunctionId)));
    const byKey = new Map();
    guests.filter(needsCard)
      .filter(r => (functionId
        ? rowFunctionIds(r).map(String).includes(String(functionId))
        : rowFunctionIds(r).some(id => open.has(String(id)))))
      .forEach(r => {
        const k = householdKey(r);
        if (!byKey.has(k)) byKey.set(k, { key: k, head: r, members: [], extraCards: 0 });
        const h = byKey.get(k);
        if (Number(r.FamilyId || 0) && String(r.GuestId) === String(r.FamilyId)) h.head = r;
        if (!h.members.some(m => String(m.GuestId) === String(r.GuestId))) h.members.push(r);
        h.extraCards = Math.max(h.extraCards, Number(r.ExtraCards || 0));
      });
    const name = (h) => String(h.head.CardName || '').trim()
      || [h.head.Surname, h.head.GuestName].filter(Boolean).join(' ').trim()
      || h.head.GuestName || 'Guest';
    const txt = (v) => String(v || '').trim().toLowerCase();
    return [...byKey.values()]
      .map(h => ({
        ...h,
        name: name(h),
        mobile: h.head.MobileNumber || '',
        city: h.head.City || '',
        group: String(h.head.GroupName || '').trim(),
        addedBy: adderName(h.head.CreatedBy),
        size: h.members.reduce((n, m) => n + Math.max(1, Number(m.ExpectedCount || 1)), 0),
      }))
      // Added by → Group → name on the card: the cards are carried round one
      // member at a time, and within a member's share one circle (Rotary, UMA …)
      // at a time. Nobody's name / no group sorts last.
      .sort((a, b) => (!a.addedBy - !b.addedBy)
        || txt(a.addedBy).localeCompare(txt(b.addedBy))
        || (!a.group - !b.group)
        || txt(a.group).localeCompare(txt(b.group))
        || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [guests, functions, adderName]);

  // What the PRINTER is ordered on: every household that gets a card at all,
  // plus the extra copies heads have asked for. One gift goes with each.
  const printRows = useMemo(() => householdList(null), [householdList]);
  const cardsToPrint = useMemo(() => printRows.length
    + printRows.reduce((n, h) => n + h.extraCards, 0), [printRows]);

  const summary = useMemo(() => [
    biggest ? { label: `Most persons (${biggest.FunctionName})`, value: biggest.persons } : null,
    { label: 'Cards to print', value: cardsToPrint },
    { label: 'Gifts (one per card)', value: cardsToPrint },
    { label: 'Paid in advance', value: money(moneyTotals.paid) },
    { label: 'Balance to pay', value: money(moneyTotals.balance) },
    moneyTotals.dueSoon > 0 ? { label: 'Due in 7 days', value: money(moneyTotals.dueSoon) } : null,
  ].filter(Boolean), [biggest, cardsToPrint, moneyTotals]);



  const listReport = useMemo(() => {
    if (!listFor) return null;

    // One line per household: the cards to give, the gifts that go with them,
    // the sweet packets to pack. A family of three takes one, the same as a
    // guest on their own.
    if (listFor.kind === 'household') {
      const hand = HANDOUTS[listFor.what] || HANDOUTS.Cards;
      const rows = householdList(listFor.id);
      const serial = new Map(rows.map((r, i) => [r, i + 1]));
      const extra = rows.reduce((n, r) => n + r.extraCards, 0);
      const persons = rows.reduce((n, r) => n + r.size, 0);
      const toGive = rows.length + (hand.withExtras ? extra : 0);
      const shortHand = adderShortNames(rows.map(r => r.addedBy));
      // The sweets list is packed and ticked off at a table: a short header,
      // no mobile / city / extra-card columns, at least 55 lines on every page,
      // and under the last line the two numbers the orders are placed on - the
      // packets, and the members coming to the lunch (owner, 2026-10-20).
      const sweets = listFor.what === 'Sweets';
      const columns = [
        // Columns in the order the list is sorted: Added by → Group → Name on
        // the card. Whoever invited the household usually carries the card to it.
        { key: 'SNo', label: '#', align: 'num', pdfWidth: 8, excelWidth: 6, exportValue: (r) => String(serial.get(r) || '') },
        // Short - "A-H" - with the key under the heading: one of four names
        // spelt out down every page takes the width the card name needs.
        { key: 'addedBy', label: 'By', align: 'center', pdfWidth: 12, excelWidth: 8, nowrap: true,
          exportValue: (r) => shortHand.get(String(r.addedBy || '').trim()) || adderInitials(r.addedBy) },
        { key: 'group', label: 'Group', pdfWidth: 24, excelWidth: 16 },
        { key: 'name', label: 'Name on the card', pdfWidth: 50, excelWidth: 28 },
        ...(sweets ? [] : [{ key: 'mobile', label: 'Mobile Number', pdfWidth: 28, excelWidth: 14, keepEmpty: true }]),
        { key: 'size', label: 'People', align: 'num', pdfWidth: 14 },
        ...(sweets ? [] : [
          { key: 'city', label: 'City', pdfWidth: 22, excelWidth: 14 },
          { key: 'extraCards', label: 'Extra cards', align: 'num', pdfWidth: 16, exportValue: (r) => (r.extraCards ? String(r.extraCards) : '') },
        ]),
      ];
      const title = `${hand.title} — ${listFor.label}`;
      const note = `${toGive} ${hand.unit} for ${rows.length} households`
        + (extra ? (hand.withExtras ? ` (${rows.length} + ${extra} extra asked for)` : ` · the ${extra} extra cards take no packet`) : '')
        + ` · ${persons} persons on them`
        + (shortHand.size ? '\nAdded by — ' + adderLegend(shortHand) : '');
      if (sweets) {
        // Members for the lunch = the function's headcount (every guest coming,
        // the hosts' own family and count-only groups included) - not only
        // the people on the cards above.
        const lunch = listFor.id ? headcountOf(listFor.id) : persons;
        return {
          rowsPerPage: 55, note,
          title: `Sweets — ${listFor.label}`,
          countText: `${toGive} packets · ${lunch} for lunch`,
          sections: [
            { columns, rows },
            {
              title: 'Totals', fitRows: false,
              // Households + the extra cards they asked for = what to order.
              columns: [
                { key: 'households', label: 'Packets for households', align: 'num', pdfWidth: 40 },
                { key: 'extra', label: 'Extra sweets for extra cards', align: 'num', pdfWidth: 40 },
                { key: 'packets', label: 'Sweet packets required', align: 'num', pdfWidth: 40 },
                { key: 'members', label: 'Members for lunch', align: 'num', pdfWidth: 40 },
              ],
              rows: [{ households: String(rows.length), extra: String(extra), packets: String(toGive), members: String(lunch) }],
            },
          ],
        };
      }
      return {
        title, note, columns, rows,
        totalsRow: { SNo: '', name: `Total ${toGive} ${hand.unit}`, size: String(persons), extraCards: extra ? String(extra) : '' },
      };
    }

    const seen = new Map();
    guests.forEach(r => {
      const keep = listFor.kind === 'function'
        ? rowFunctionIds(r).map(String).includes(String(listFor.id))
        : String(r.CreatedBy || '') === String(listFor.id);
      if (!keep) return;
      if (!seen.has(String(r.GuestId))) seen.set(String(r.GuestId), r);
    });
    const txt = (v) => String(v || '').trim().toLowerCase();
    // Added by → Surname → Name: the list is worked through one member at a
    // time, so their guests stand together (nobody's name last).
    const by = (r) => txt(adderName(r.CreatedBy));
    const rows = [...seen.values()].sort((a, b) =>
      (!by(a) - !by(b)) || by(a).localeCompare(by(b))
      || txt(a.Surname).localeCompare(txt(b.Surname))
      || txt(a.GuestName).localeCompare(txt(b.GuestName)));
    const serial = new Map(rows.map((r, i) => [r, i + 1]));
    const persons = rows.reduce((n, r) => n + Math.max(1, Number(r.ExpectedCount || 1)), 0);
    const households = new Set(rows.filter(needsCard).map(householdKey)).size;
    const isFn = listFor.kind === 'function';
    const shortFn = isFn ? adderShortNames(rows.map(r => adderName(r.CreatedBy))) : new Map();
    return {
      title: isFn ? `Invited for — ${listFor.label}` : `Guests added by ${listFor.label}`,
      note: `${rows.length} ${rows.length === 1 ? 'guest' : 'guests'} · ${persons} persons · ${households} cards`
        + (shortFn.size ? '\nAdded by — ' + adderLegend(shortFn) : ''),
      columns: [
        { key: 'SNo', label: 'S.No', align: 'num', pdfWidth: 9, excelWidth: 6, exportValue: (r) => String(serial.get(r) || '') },
        { key: 'GroupName', label: 'Group', pdfWidth: 24, excelWidth: 16,
          exportValue: (r) => String(r.GroupName || '').trim() },
        { key: 'Surname', label: 'Surname', pdfWidth: 30, excelWidth: 16 },
        { key: 'GuestName', label: 'Name', pdfWidth: 34, excelWidth: 20 },
        { key: 'MobileNumber', label: 'Mobile Number', pdfWidth: 28, excelWidth: 14, keepEmpty: true },
        { key: 'City', label: 'City', pdfWidth: 20, excelWidth: 14 },
        { key: 'ExpectedCount', label: 'Persons', align: 'num', pdfWidth: 14,
          exportValue: (r) => String(Math.max(1, Number(r.ExpectedCount || 1))) },
        // Who invited them - this is the admin's working list, not the printed
        // list that is handed around, so the name earns its place here. One
        // person's own list leaves it out: it would be the same name all the
        // way down.
        ...(isFn ? [{ key: 'AddedByName', label: 'By', align: 'center', pdfWidth: 12, excelWidth: 8, nowrap: true,
          exportValue: (r) => shortFn.get(adderName(r.CreatedBy)) || adderInitials(adderName(r.CreatedBy)) }] : []),
        // One function's list: every row would repeat the same answer, and the
        // header line already says which function it is. One person's list
        // keeps it - there the functions differ from guest to guest.
        ...(isFn ? [] : [{ key: 'FunctionNames', label: 'Invited to', pdfWidth: 60, excelWidth: 30,
          exportValue: (r) => String(r.FunctionNames || r.FunctionName || '') }]),
      ],
      rows,
      totalsRow: { SNo: '', Surname: 'Total', GuestName: `${rows.length} guests`, ExpectedCount: String(persons) },
    };
  }, [listFor, guests, householdList, adderName, headcountOf]);

  const report = useMemo(() => {
    const sections = [];
    if (blocks.members) sections.push({
      title: BLOCK_LABELS.members, note: fnNote || undefined,
      columns: memberColumns, rows: memberShown,
      totalsRow: {
        FunctionName: 'Total', cards: memberTotals.cards, parties: memberTotals.parties,
        named: memberTotals.named, extra: memberTotals.extra, persons: memberTotals.persons,
        groom: memberTotals.groom, bride: memberTotals.bride,
      },
    });
    if (blocks.invitedBy) sections.push({
      title: BLOCK_LABELS.invitedBy, columns: invitedByColumns, rows: invitedByRows,
      totalsRow: {
        name: 'Total', guests: invitedByTotals.guests, persons: invitedByTotals.persons,
        totalCards: invitedByTotals.totalCards,
        ...Object.fromEntries(shownFunctions.map(f => [`fn${f.FunctionId}`,
          invitedByRows.reduce((s, r) => s + (r.perFn[String(f.FunctionId)] || 0), 0)])),
      },
    });
    if (blocks.cards) sections.push({
      title: BLOCK_LABELS.cards,
      note: [`${cardStats.households} households`,
        cardStats.extraCards ? `${cardStats.extraCards} extra cards (${cardStats.extraPersons} persons)` : '',
        cardStats.ownFamily ? `${cardStats.ownFamily} own family — no card` : '',
        cardStats.withoutType ? `${cardStats.withoutType} households with no card type yet` : ''].filter(Boolean).join(' · '),
      columns: cardColumns, rows: cardStats.rows,
      totalsRow: {
        name: 'Total to order', households: cardStats.rows.reduce((s, r) => s + r.households, 0),
        extra: cardStats.rows.reduce((s, r) => s + r.extra, 0), total: cardStats.toOrder,
      },
    });
    if (blocks.money) sections.push({
      title: BLOCK_LABELS.money,
      note: [catNote, withEstimates ? 'estimates included' : 'estimates left out'].filter(Boolean).join(' · '),
      columns: moneyColumns, rows: moneyShown,
      totalsRow: {
        category: 'Total', total: money(moneyTotals.total), paid: money(moneyTotals.paid),
        balance: money(moneyTotals.balance), estimates: moneyTotals.estimates || '',
      },
    });
    if (blocks.bookings && bookingRows.length) sections.push({
      title: BLOCK_LABELS.bookings,
      note: 'A minimum is charged at the live count when more come; an order is charged as ordered',
      columns: bookingColumns, rows: bookingRows,
    });
    return {
      title: 'Brief Report',
      subtitle: event?.EventName || '',
      summary,
      sections,
    };
  }, [blocks, fnNote, catNote, withEstimates, event, summary, bookingRows, bookingColumns,
    memberColumns, memberShown, memberTotals,
    invitedByColumns, invitedByRows, invitedByTotals, shownFunctions,
    cardColumns, cardStats, moneyColumns, moneyShown, moneyTotals]);

  return {
    canView, loading, event, reload: load, twoSided,
    listFor, openList, closeList, listReport, householdList, printRows, cardsToPrint, sweetsFunction,
    blocks, toggleBlock, blockLabels: BLOCK_LABELS,
    functions: liveFunctions, pastFunctions, fnOff, toggleFunction, shownFunctions,
    catOff, toggleCategory, withEstimates, setWithEstimates,
    memberRows, memberShown, memberTotals, memberColumns, biggest,
    invitedByRows, invitedByTotals, invitedByColumns,
    cardStats, cardColumns,
    moneyRows, moneyShown, moneyTotals, moneyColumns,
    bookingRows, bookingColumns,
    summary, report, money,
  };
}
